import dayjs, { type Dayjs } from '@atlas/tool/box/date/dayjs';

/**
 *   A wrapper class for handling date and time operations in the Atlas application.
 * This class provides convenient methods for parsing, formatting, and displaying dates
 * with specific timezone handling.
 *
 * @class
 * @example
 * ```typescript
 * // Create from UTC string
 * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
 *
 * // Format the date
 * console.log(dateTime.fullDateTime); // "Dec 25, 2023, 10:30 AM"
 *
 * // Get relative time
 * console.log(dateTime.relative); // "2 hours ago"
 * ```
 */
class AtlasDateTime {
    private _date: Dayjs;

    constructor(d: Dayjs) {
        this._date = d;
    }

    static fromUTCString(utcString: string) {
        const dayjsUtc = dayjs(typeof utcString === 'string' && utcString.endsWith('Z') ? utcString : utcString + 'Z');

        return new AtlasDateTime(dayjsUtc);
    }

    static getDateRange(start: Dayjs, end: Dayjs) {
        const atlasStart = new AtlasDateTime(start);

        return `${atlasStart.shortDate} between ${start.format('h:mm A')} and ${end.format('h:mm A')}`;
    }

    private format(format: DateFormat) {
        const formatBody = dateFormats[format];
        const now = dayjs();
        const isToday = this._date.isSame(now, 'day');

        if (isToday) {
            return this._date.format(formatBody.current);
        }

        const isThisYear = this._date.isSame(now, 'year');

        if (isThisYear) {
            return this._date.format(formatBody.priorDay || formatBody.current);
        }

        return this._date.format(formatBody.priorYear || formatBody.current);
    }

    /**
     * Returns the date in the short date format.
     * @returns {string} The date in the format "MMM D" or "MMM D, YYYY" if the date is not in the current year.
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.shortDate); // "Dec 25, 2023"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.shortDate); // "Dec 25"
     * ```
     */
    get shortDate() {
        return this.format(DateFormat.SHORT_DATE);
    }

    /**
     * Returns the date in the short date time format.
     * @returns {string} The date in the format "h:mm A", "MMM D, h:mm A", or "MMM D, YYYY, h:mm A" depending on prior year or day.
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.shortDateTime); // "10:30 AM"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.shortDateTime); // "Dec 25, 10:30 AM"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.shortDateTime); // "Dec 25, 2023, 10:30 AM"
     * ```
     */
    get shortDateTime() {
        return this.format(DateFormat.SHORT_DATE_TIME);
    }

    /**
     * Returns the date in the full date time format.
     * @returns {string} The date in the format "MMM D, YYYY, h:mm A".
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.fullDateTime); // "Dec 25, 2023, 10:30 AM"
     * ```
     */
    get fullDateTime() {
        return this.format(DateFormat.FULL_DATE_TIME);
    }

    /**
     * Returns the date in the tiny date time format.
     * @returns {string} The date in the format "h:mm A", "MMM D", or "MMM D, YYYY" depending on prior year or day.
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.tinyDateTime); // "10:30 AM"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.tinyDateTime); // "Dec 25"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.tinyDateTime); // "Dec 25, 2023"
     * ```
     */
    get tinyDateTime() {
        return this.format(DateFormat.TINY_DATE_TIME);
    }

    private getRelativeString(diff: number, unit: RelativeStringUnit) {
        return `${diff} ${unit}${diff > 1 ? 's' : ''} ago`;
    }

    /**
     * Returns the relative time from the current date.
     * @returns {string} The relative time in the format "Now", "X minute(s) ago", "X hour(s) ago", or "X day(s) ago".
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.relative); // "2 hours ago"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.relative); // "2 days ago"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.relative); // "Now"
     * ```
     */
    get relative() {
        const minutesDiff = dayjs().diff(this._date, 'minute');

        if (minutesDiff < 2) {
            return 'Now';
        }

        const hoursDiff = dayjs().diff(this._date, 'hour');

        if (hoursDiff < 1) {
            return this.getRelativeString(minutesDiff, 'minute');
        }

        const daysDiff = dayjs().diff(this._date, 'day');

        if (daysDiff < 1) {
            return this.getRelativeString(hoursDiff, 'hour');
        }

        return this.getRelativeString(daysDiff, 'day');
    }
}

export default AtlasDateTime;

// #region Types

export enum DateFormat {
    SHORT_DATE = 'shortDate',
    SHORT_DATE_TIME = 'shortDateTime',
    FULL_DATE_TIME = 'fullDateTime',
    TINY_DATE_TIME = 'tinyDateTime',
}

type DateFormatBody = {
    current: string;
    priorDay?: string;
    priorYear?: string;
};

type RelativeStringUnit = 'minute' | 'hour' | 'day';

// #endregion

// #region Helpers

const dateFormats: Record<DateFormat, DateFormatBody> = {
    [DateFormat.SHORT_DATE]: {
        current: 'MMM D',
        priorYear: 'MMM D, YYYY',
    },
    [DateFormat.SHORT_DATE_TIME]: {
        current: 'h:mm A',
        priorDay: 'MMM D, h:mm A',
        priorYear: 'MMM D, YYYY, h:mm A',
    },
    [DateFormat.FULL_DATE_TIME]: {
        current: 'MMM D, YYYY, h:mm A',
    },
    [DateFormat.TINY_DATE_TIME]: {
        current: 'h:mm A',
        priorDay: 'MMM D',
        priorYear: 'MMM D, YYYY',
    },
};

// #endregion
